import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";

import {
  AppState,
  loadedMenusState,
} from "src/app/store/reducers";
import {
  getSitesList,
  getSitesExcelList,
  resetSitesState,
} from "src/app/store/actions/sites/sites.actions";

import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";

import {
  DefaultPage,
  TableEvent,
  ExportEvent,
  FieldType,
  PageEvent,
  SortEvent,
  SearchEvent,
  FilterEvent,
  ColumnSorterInfo,
} from "src/app/models/custom-table/custom-table.model";

import { subMenusIterator } from "src/app/shared/utils/menuUtils";

import { BaseSitesList } from "src/app/shared/base-classes/base-sites-list/base-sites-list";
import { TranslateService } from "@ngx-translate/core";

import { WetstockRowDetailComponent } from "../wetstock-row-detail/wetstock-row-detail.component";

const USAGE_CODE = "WETSTOCK";


@Component({
  selector: 'app-wetstock-list',
  templateUrl: './wetstock-list.component.html',
  styleUrls: ['./wetstock-list.component.scss']
})
export class WetstockListComponent extends BaseSitesList implements OnInit, OnDestroy {
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  headerIcon: string;
  tableTitle: string;

  wetstockRowDetailComponent = WetstockRowDetailComponent;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    store: Store<AppState>,
    translate: TranslateService
  ) {
    super(store, translate);

    this.tableConfig = [
      {
        header: "SiteManagement.Code",
        field: "code",
        fieldType: FieldType.STRING,
        hidden: true,
        identifier: true,
      },
      {
        header: "SiteManagement.SiteNumber",
        field: "siteNumber",
        fieldType: FieldType.STRING,
        searchable: true,
        style: "font-weight: 600",
      },
      {
        header: "SiteManagement.Description",
        field: "description",
        fieldType: FieldType.STRING,
        searchable: true,
      },
      {
        header: "SiteManagement.RegionDescription",
        field: "region.description",
        fieldType: FieldType.CHIP_STRING,
        searchable: true,
      },
      {
        header: "SiteManagement.Enabled",
        field: "enabled",
        fieldType: FieldType.BOOLEAN,
      },
    ];
  }

  ngOnInit(): void {
    this.getTableConfig(USAGE_CODE);
    this.getSitesList();
    this.setTableCustomElements();
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.loadScreenTitle();
  }

  override getSitesCategoriesProperties(usageCode: string, getSites: Function) {
    this.getTableConfig(usageCode);
    getSites();
    this.setTableCustomElements();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          })
        });
      }
    });
  }

  getSitesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSitesList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getSitesExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getSitesExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getSitesList.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getSitesExcelList.bind(this));
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(tableColumnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
  }
}

